import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, ScanFace, PenLine, CheckCircle2, RotateCcw } from "lucide-react";

const SCAN_SECONDS = 5;

export default function AutographModal({ copy, proName, onClose, onSigned }) {
  const [step, setStep] = useState("scan");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [hasInk, setHasInk] = useState(false);
  const [countdown, setCountdown] = useState(SCAN_SECONDS);
  const [scanState, setScanState] = useState("starting");
  const [scanCost, setScanCost] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timersRef = useRef([]);
  const doneRef = useRef(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    api.get("/autograph/scan-cost").then(({ data }) => setScanCost(data)).catch(() => {});
  }, []);

  const stopCamera = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const grabFrame = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const scale = Math.min(1, 800 / Math.max(v.videoWidth, v.videoHeight));
    const cv = document.createElement("canvas");
    cv.width = Math.round(v.videoWidth * scale);
    cv.height = Math.round(v.videoHeight * scale);
    cv.getContext("2d").drawImage(v, 0, 0, cv.width, cv.height);
    return cv.toDataURL("image/jpeg", 0.82);
  };

  const verify = async (frame1, frame2) => {
    doneRef.current = true;
    stopCamera();
    setScanState("verifying");
    setBusy(true);
    try {
      const { data } = await api.post("/autograph/verify",
        { copy_id: copy.id, image: frame1, image2: frame2 }, { timeout: 90000 });
      setSession(data);
      setStep("sign");
      toast.success(`${data.pro_name} verified — ${data.confidence}% match. Signing window open for 5 minutes.`
        + (data.charged > 0 ? ` (${data.charged} BLC scan fee)` : ""));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verification failed — start over");
      onClose();
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (step !== "scan") return undefined;
    let cancelled = false;
    doneRef.current = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 960 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        v.srcObject = stream;
        await v.play();
        setScanState("scanning");
        let frame1 = null;
        for (let s = 1; s <= SCAN_SECONDS; s += 1) {
          timersRef.current.push(setTimeout(() => setCountdown(SCAN_SECONDS - s), s * 1000));
        }
        timersRef.current.push(setTimeout(() => { frame1 = grabFrame(); }, 1600));
        timersRef.current.push(setTimeout(() => {
          const frame2 = grabFrame();
          if (frame1 && frame2) verify(frame1, frame2);
        }, 2800));
        timersRef.current.push(setTimeout(() => {
          if (!doneRef.current) {
            stopCamera();
            toast.error("Verification scan timed out — tap Get Signed to start over");
            onClose();
          }
        }, SCAN_SECONDS * 1000));
      } catch {
        toast.error("Couldn't open the front camera — allow camera access and try again");
        onClose();
      }
    })();
    return () => { cancelled = true; stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step !== "sign") return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#ffffff";
    const pos = (ev) => {
      const r = cv.getBoundingClientRect();
      return [(ev.clientX - r.left) * (cv.width / r.width), (ev.clientY - r.top) * (cv.height / r.height)];
    };
    const down = (ev) => { drawing.current = true; const [x, y] = pos(ev); ctx.beginPath(); ctx.moveTo(x, y); ev.preventDefault(); };
    const move = (ev) => { if (!drawing.current) return; const [x, y] = pos(ev); ctx.lineTo(x, y); ctx.stroke(); setHasInk(true); ev.preventDefault(); };
    const up = () => { drawing.current = false; };
    cv.addEventListener("pointerdown", down);
    cv.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { cv.removeEventListener("pointerdown", down); cv.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [step]);

  const clearInk = () => {
    const cv = canvasRef.current;
    cv.getContext("2d").clearRect(0, 0, cv.width, cv.height);
    setHasInk(false);
  };

  const bake = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/autograph/sign",
        { token: session.token, signature: canvasRef.current.toDataURL("image/png") }, { timeout: 90000 });
      setSignedUrl(data.url);
      setStep("done");
      toast.success("Signature baked in — it's in the registry forever.");
      onSigned?.(copy.id, data.url, data.autograph);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/90 flex items-center justify-center p-4 overflow-y-auto" data-testid="autograph-modal">
      <div className="bg-pb-surface border border-yellow-500/50 max-w-md w-full p-5 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 font-display font-black uppercase text-lg">
            <PenLine className="w-4 h-4 text-yellow-400" /> Verified Autograph
          </div>
          <button data-testid="autograph-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">
          {copy.subject_name} · signed by {proName}
        </div>

        {step === "scan" && (
          <div className="space-y-3" data-testid="autograph-scan-step">
            {scanCost && (
              <div data-testid="autograph-scan-cost" className={`text-[10px] font-black uppercase tracking-widest px-3 py-2 border ${scanCost.cost_now > 0 ? "border-yellow-500/50 bg-yellow-500/10 text-yellow-300" : "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"}`}>
                {scanCost.cost_now > 0 ? `This verification scan costs ${scanCost.price} BLC` : "Your first verification scan is FREE"}
              </div>
            )}
            <p className="text-xs text-zinc-400">
              Point the camera at <span className="text-yellow-300 font-bold">{proName}</span> — the scan is
              automatic. The Bullpen verifies it's really them; a verified match opens the signing box.
              Each card can only ever be signed once.
            </p>
            <div className="relative">
              <video ref={videoRef} playsInline muted autoPlay
                className="w-full h-56 object-cover bg-black border-2 border-yellow-500/50 [transform:scaleX(-1)]" data-testid="autograph-video" />
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-36 h-44 border-2 border-yellow-400/70 rounded-[50%]" />
              </div>
              {scanState === "scanning" && (
                <div className="absolute top-2 right-2 w-9 h-9 flex items-center justify-center rounded-full bg-black/80 border border-yellow-400 text-yellow-300 font-mono font-black" data-testid="autograph-countdown">
                  {countdown}
                </div>
              )}
              <div className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-black/80 px-2 py-1 text-[9px] font-black uppercase tracking-widest text-yellow-300" data-testid="autograph-scan-state">
                {scanState === "starting" && <><Loader2 className="w-3 h-3 animate-spin" /> Opening camera…</>}
                {scanState === "scanning" && <><ScanFace className="w-3 h-3 animate-pulse" /> Scanning — hold steady</>}
                {scanState === "verifying" && <><Loader2 className="w-3 h-3 animate-spin" /> Verifying {proName}…</>}
              </div>
            </div>
            <p className="text-[9px] text-zinc-600 uppercase tracking-widest text-center">
              Auto-captures 2 live frames · times out in {SCAN_SECONDS}s · no photos accepted
            </p>
          </div>
        )}

        {step === "sign" && (
          <div className="space-y-3" data-testid="autograph-sign-step">
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold" data-testid="autograph-verified-banner">
              <CheckCircle2 className="w-4 h-4" /> {session.pro_name} verified — {session.confidence}% match
            </div>
            <p className="text-xs text-zinc-400">Sign inside the box with a stylus or finger. This ink gets baked into the card forever.</p>
            <canvas ref={canvasRef} width={640} height={280} data-testid="autograph-canvas"
              className="w-full bg-[#0c0e10] border-2 border-yellow-500/50 touch-none cursor-crosshair" style={{ touchAction: "none" }} />
            <div className="flex gap-2">
              <button data-testid="autograph-clear" onClick={clearInk}
                className="flex items-center gap-1.5 border border-pb-border text-zinc-400 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest hover:border-zinc-500">
                <RotateCcw className="w-3 h-3" /> Clear
              </button>
              <button data-testid="autograph-bake-btn" onClick={bake} disabled={!hasInk || busy}
                className="flex-1 flex items-center justify-center gap-2 bg-yellow-400 text-black font-black uppercase tracking-widest py-2.5 text-xs hover:bg-yellow-300 transition-colors disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Bake it into the card
              </button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-3 text-center" data-testid="autograph-done-step">
            <img src={signedUrl} alt="signed card" className="w-full max-h-96 object-contain bg-black border border-yellow-500/60" data-testid="autograph-signed-img" />
            <div className="text-yellow-300 font-display font-black uppercase">Verified Fan Signed Card</div>
            <p className="text-xs text-zinc-500">Verification, signature and bake are all timestamped in the card's permanent registry. This card is now locked — it can never be signed again.</p>
            <button data-testid="autograph-done-close" onClick={onClose}
              className="w-full bg-yellow-400 text-black font-black uppercase tracking-widest py-2.5 text-xs hover:bg-yellow-300">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
